import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import usePullToRefresh from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LongTermAdvances = () => {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAdvanceOpen, setNewAdvanceOpen] = useState(false);
  const [expandedAdvance, setExpandedAdvance] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [selectedAdvanceId, setSelectedAdvanceId] = useState(null);

  const [newAdvance, setNewAdvance] = useState({
    employee_name: '',
    total_amount: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    installment_amount: '',
    description: ''
  });

  const [payment, setPayment] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    voucher_number: ''
  });

  useEffect(() => {
    fetchAdvances();
  }, []);

  const fetchAdvances = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/long-term-advances`);
      setAdvances(response.data);
    } catch (error) {
      console.error('Error fetching advances:', error);
      toast.error('Failed to load advances');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    toast.info('Refreshing...');
    await fetchAdvances();
    toast.success('Data refreshed!');
  }, [fetchAdvances]);

  const { pullDistance, isRefreshing, showIndicator } = usePullToRefresh(handleRefresh);

  const handleCreateAdvance = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/long-term-advances`, {
        ...newAdvance,
        total_amount: parseFloat(newAdvance.total_amount),
        installment_amount: parseFloat(newAdvance.installment_amount)
      });
      toast.success('Long-term advance created successfully');
      setNewAdvanceOpen(false);
      setNewAdvance({
        employee_name: '',
        total_amount: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        installment_amount: '',
        description: ''
      });
      fetchAdvances();
    } catch (error) {
      console.error('Error creating advance:', error);
      toast.error('Failed to create advance');
    }
  };

  const handleMarkPayment = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/long-term-advances/${selectedAdvanceId}/mark-payment`, {
        month: selectedInstallment.month,
        payment_date: payment.payment_date,
        voucher_number: payment.voucher_number
      });
      toast.success('Payment marked successfully');
      setPaymentDialogOpen(false);
      setPayment({ payment_date: new Date().toISOString().split('T')[0], voucher_number: '' });
      setSelectedInstallment(null);
      setSelectedAdvanceId(null);
      fetchAdvances();
    } catch (error) {
      console.error('Error marking payment:', error);
      toast.error('Failed to mark payment');
    }
  };

  const openPaymentDialog = (advanceId, installment) => {
    setSelectedAdvanceId(advanceId);
    setSelectedInstallment(installment);
    setPaymentDialogOpen(true);
  };

  const toggleAdvance = (advanceId) => {
    setExpandedAdvance(expandedAdvance === advanceId ? null : advanceId);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="long-term-loading"><div className="text-slate-500">Loading...</div></div>;
  }

  return (
    <div className="animate-in" data-testid="long-term-advances">
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-2">Long-term Advances</h1>
          <p className="text-base text-slate-600">Manage advances with monthly installments</p>
        </div>
        <Dialog open={newAdvanceOpen} onOpenChange={setNewAdvanceOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="new-long-term-advance-button"
              className="bg-slate-900 text-white hover:bg-slate-800 shadow-sm font-medium transition-all active:scale-95"
            >
              <Plus size={20} className="mr-2" />
              New Long-term Advance
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="new-long-term-dialog">
            <DialogHeader>
              <DialogTitle>Create Long-term Advance</DialogTitle>
              <DialogDescription>Setup a new advance with monthly installments</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAdvance} className="space-y-4">
              <div>
                <Label htmlFor="lt_employee_name">Employee Name</Label>
                <Input
                  id="lt_employee_name"
                  data-testid="input-lt-employee-name"
                  value={newAdvance.employee_name}
                  onChange={(e) => setNewAdvance({ ...newAdvance, employee_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Description / Reason for Advance</Label>
                <Textarea
                  id="description"
                  data-testid="input-description"
                  placeholder="e.g., Medical emergency, Home renovation, Educational expenses, etc."
                  value={newAdvance.description}
                  onChange={(e) => setNewAdvance({ ...newAdvance, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="total_amount">Total Amount (₹)</Label>
                <Input
                  id="total_amount"
                  type="number"
                  step="0.01"
                  data-testid="input-total-amount"
                  value={newAdvance.total_amount}
                  onChange={(e) => setNewAdvance({ ...newAdvance, total_amount: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="installment_amount">Monthly Installment (₹)</Label>
                <Input
                  id="installment_amount"
                  type="number"
                  step="0.01"
                  data-testid="input-installment-amount"
                  value={newAdvance.installment_amount}
                  onChange={(e) => setNewAdvance({ ...newAdvance, installment_amount: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    data-testid="input-start-date"
                    value={newAdvance.start_date}
                    onChange={(e) => setNewAdvance({ ...newAdvance, start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    data-testid="input-end-date"
                    value={newAdvance.end_date}
                    onChange={(e) => setNewAdvance({ ...newAdvance, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded-md">
                <p>Number of installments will be calculated automatically based on start and end dates.</p>
              </div>
              <Button type="submit" data-testid="submit-long-term" className="w-full bg-slate-900 text-white hover:bg-slate-800">
                Create Advance
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {advances.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 text-center text-slate-500" data-testid="no-long-term-advances">
            No long-term advances found
          </div>
        ) : (
          advances.map((advance) => {
            const paidCount = advance.installments.filter(i => i.paid).length;
            const totalCount = advance.installments.length;
            const isExpanded = expandedAdvance === advance.id;

            return (
              <div key={advance.id} data-testid={`long-term-advance-${advance.id}`} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div
                  className="p-6 cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between"
                  onClick={() => toggleAdvance(advance.id)}
                  data-testid={`toggle-advance-${advance.id}`}
                >
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{advance.employee_name}</h3>
                    {advance.description && (
                      <p className="text-sm text-slate-600 mb-3 italic">"{advance.description}"</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500">Total Amount</p>
                        <p className="font-semibold text-slate-900 tabular-nums">₹{advance.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Monthly Installment</p>
                        <p className="font-semibold text-slate-900 tabular-nums">₹{advance.installment_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Progress</p>
                        <p className="font-semibold text-slate-900">{paidCount} / {totalCount}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Duration</p>
                        <p className="font-semibold text-slate-900 text-xs">{advance.start_date} to {advance.end_date}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    {isExpanded ? <ChevronUp size={24} className="text-slate-500" /> : <ChevronDown size={24} className="text-slate-500" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-200 p-4 md:p-6 bg-slate-50/50">
                    <h4 className="text-lg font-semibold text-slate-900 mb-4">Installment Schedule</h4>
                    
                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-3">
                      {advance.installments.map((installment, idx) => (
                        <div key={idx} data-testid={`installment-card-${idx}`} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="font-semibold text-slate-900">{installment.month}</p>
                              <p className="text-xs text-slate-500">Due: {installment.due_date}</p>
                            </div>
                            {installment.paid ? (
                              <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded-full text-xs font-medium">Paid</span>
                            ) : (
                              <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-full text-xs font-medium">Pending</span>
                            )}
                          </div>
                          <div className="border-t border-slate-100 pt-3">
                            <p className="text-sm text-slate-600 mb-2">
                              Amount: <span className="font-semibold text-slate-900 tabular-nums">₹{advance.installment_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                            </p>
                            {installment.paid ? (
                              <div className="bg-emerald-50 rounded-lg p-3 text-xs">
                                <p className="text-emerald-700"><span className="font-semibold">Paid on:</span> {installment.payment_date}</p>
                                <p className="text-emerald-700"><span className="font-semibold">Voucher:</span> {installment.voucher_number}</p>
                              </div>
                            ) : (
                              <Button
                                data-testid={`mark-payment-button-mobile-${idx}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openPaymentDialog(advance.id, installment);
                                }}
                                className="w-full bg-blue-600 text-white hover:bg-blue-700 py-5 text-sm font-semibold"
                              >
                                Mark Paid
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Month</th>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Due Date</th>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Amount</th>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Status</th>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Payment Info</th>
                            <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider p-4">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {advance.installments.map((installment, idx) => (
                            <tr key={idx} data-testid={`installment-row-${idx}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                              <td className="p-4 text-sm text-slate-700 font-medium">{installment.month}</td>
                              <td className="p-4 text-sm text-slate-700">{installment.due_date}</td>
                              <td className="p-4 text-sm text-slate-700 tabular-nums">₹{advance.installment_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                              <td className="p-4 text-sm">
                                {installment.paid ? (
                                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full text-xs font-medium">Paid</span>
                                ) : (
                                  <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full text-xs font-medium">Pending</span>
                                )}
                              </td>
                              <td className="p-4 text-sm text-slate-700">
                                {installment.paid ? (
                                  <div>
                                    <div className="text-xs">{installment.payment_date}</div>
                                    <div className="text-xs text-slate-500">Voucher: {installment.voucher_number}</div>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="p-4 text-sm">
                                {!installment.paid && (
                                  <Button
                                    data-testid={`mark-payment-button-${idx}`}
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPaymentDialog(advance.id, installment);
                                    }}
                                    className="bg-blue-600 text-white hover:bg-blue-700 text-xs"
                                  >
                                    Mark Paid
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle>Mark Payment</DialogTitle>
            <DialogDescription>Record the payment for this installment</DialogDescription>
          </DialogHeader>
          {selectedInstallment && (
            <form onSubmit={handleMarkPayment} className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-md">
                <p className="text-sm text-slate-600">Month: <span className="font-semibold text-slate-900">{selectedInstallment.month}</span></p>
                <p className="text-sm text-slate-600">Due Date: <span className="font-semibold text-slate-900">{selectedInstallment.due_date}</span></p>
              </div>
              <div>
                <Label htmlFor="payment_date">Payment Date</Label>
                <Input
                  id="payment_date"
                  type="date"
                  data-testid="input-payment-date"
                  value={payment.payment_date}
                  onChange={(e) => setPayment({ ...payment, payment_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="payment_voucher">Voucher Number</Label>
                <Input
                  id="payment_voucher"
                  data-testid="input-payment-voucher"
                  value={payment.voucher_number}
                  onChange={(e) => setPayment({ ...payment, voucher_number: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" data-testid="submit-payment" className="w-full bg-slate-900 text-white hover:bg-slate-800">
                Mark as Paid
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LongTermAdvances;